import { DiscountCodeInput } from "@/components/discount-code-input";
import { getOwnDiscounts, applySubscriptionDiscount } from "@/lib/discount.functions";
import {
  discountCopy,
  discountError,
  type DiscountSelection,
  type OwnDiscount,
} from "@/lib/discount.types";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  Clock3,
  Download,
  KeyRound,
  Landmark,
  LockKeyhole,
  LogOut,
  Mail,
  MapPin,
  Phone,
  Pencil,
  ReceiptText,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  FORM_TEXT_MAX_LENGTH,
  FORM_TEXT_MIN_LENGTH,
  FormTextCounter,
} from "@/components/form-text-counter";
import { CustomerFeedbackCard } from "@/components/customer-feedback-card";
import { CompanyInterests } from "@/components/jobs/company-interests";
import { JobCompanySelection } from "@/components/jobs/job-company-selection";
import { JobNotifications } from "@/components/jobs/job-notifications";
import { InterestReturnNotice } from "@/components/jobs/interest-return-notice";
import { authCopy } from "@/lib/auth.copy";
import { getAuthStatus, signOutProvider, updateOwnAccountProfile } from "@/lib/auth.functions";
import { billingPlanName, fallbackBillingPlans } from "@/lib/billing.catalog";
import {
  getOwnBillingOverview,
  requestOwnBillingInvoice,
  setOwnBillingCancellation,
} from "@/lib/billing.functions";
import { downloadBillingCreditNotePdf, downloadBillingInvoicePdf } from "@/lib/billing-pdf";
import {
  formatBillingPeriod,
  formatBillingPrice,
  type BillingAccess,
  type BillingOverview,
} from "@/lib/billing.types";
import {
  getOwnAccountJobs,
  saveOwnCustomerFeedback,
  updateOwnAccountJob,
  type AccountJob,
} from "@/lib/customer-account.functions";
import {
  getOwnProviderInternalFeedback,
  saveOwnProviderInternalFeedback,
  type ProviderInternalFeedback,
} from "@/lib/provider-feedback.functions";
import {
  isSafeInternalPath,
  type ProviderAccountRole,
  type ProviderStatus,
} from "@/lib/auth.types";
import { useLanguage, type LanguageCode } from "@/lib/i18n";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/konto")({
  staticData: { sitemap: false, toasts: true },
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    next:
      typeof search.next === "string" && isSafeInternalPath(search.next) ? search.next : undefined,
  }),
  loader: () => getAuthStatus(),
  pendingComponent: AccountLoading,
  head: () => ({
    meta: [
      { title: "Mein Begner-Konto" },
      {
        name: "description",
        content: "Eigene Aufträge und persönliche Begner-Kontodaten verwalten.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AccountPage,
});

function AccountLoading() {
  const { language } = useLanguage();
  return (
    <div className="container-page flex min-h-[60vh] items-center justify-center py-20 text-muted-foreground">
      <span className="mr-3 size-2 animate-pulse rounded-full bg-primary" />
      {authCopy[language].account.loading}
    </div>
  );
}

function AccountPage() {
  const status = Route.useLoaderData();
  const { next } = Route.useSearch();
  const navigate = Route.useNavigate();
  const logout = useServerFn(signOutProvider);
  const loadBillingOverview = useServerFn(getOwnBillingOverview);
  const { language } = useLanguage();
  const copy = authCopy[language].account;
  const [loggingOut, setLoggingOut] = useState(false);
  const [billingOverview, setBillingOverview] = useState<BillingOverview | null>(null);

  useEffect(() => {
    if (!status.authenticated) {
      const destination = next ? `/konto?next=${encodeURIComponent(next)}#billing` : "/konto";
      void navigate({
        to: "/login",
        search: { mode: "anmelden", next: destination },
        replace: true,
      });
    }
  }, [navigate, status.authenticated, next]);

  useEffect(() => {
    if (!status.authenticated || status.profile?.role === "kunde") return;
    let active = true;
    void loadBillingOverview()
      .then((overview) => {
        if (active) setBillingOverview(overview);
      })
      .catch(() => {
        if (active) setBillingOverview({ invoices: [], subscription: null, settings: null });
      });
    return () => {
      active = false;
    };
  }, [loadBillingOverview, status]);

  if (!status.authenticated) return <AccountLoading />;

  const profile = status.profile;
  const isAgent = profile?.role === "immobilienmakler";
  const providerStatus: ProviderStatus = profile?.providerStatus ?? "pending";
  const displayName = [profile?.firstName, profile?.lastName].filter(Boolean).join(" ");

  async function handleLogout() {
    setLoggingOut(true);
    try {
      const result = await logout();
      if (result.ok) {
        await navigate({ to: "/login", search: { mode: "anmelden" }, replace: true });
      }
    } finally {
      setLoggingOut(false);
    }
  }

  if (profile?.role === "kunde") {
    return (
      <CustomerAccountView
        email={status.user.email}
        firstName={profile.firstName}
        lastName={profile.lastName}
        phone={profile.phone}
        loggingOut={loggingOut}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <div className="relative isolate overflow-hidden bg-muted/25 py-9 sm:py-20">
      <div className="pointer-events-none absolute left-1/2 top-0 -z-10 h-80 w-[70rem] -translate-x-1/2 rounded-full bg-brand-soft/80 blur-3xl" />
      <div className="container-page">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-background/80 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-brand-deep shadow-sm">
              <UserRound className="size-4" />
              {copy.eyebrow}
            </div>
            <h1 className="mt-5 max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-5xl">
              {isAgent ? copy.agentTitle : copy.title}
            </h1>
            <p className="mt-3 max-w-2xl text-lg text-muted-foreground">
              {isAgent ? copy.agentIntro : copy.intro}
            </p>
            <div className="mt-5 inline-flex max-w-full items-center gap-3 rounded-xl border border-primary/15 bg-background/80 px-3.5 py-2.5 shadow-sm">
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-brand-soft text-brand-deep">
                <UserRound className="size-4" />
              </span>
              <span className="min-w-0 text-left">
                <span className="block text-xs font-medium text-muted-foreground">
                  {copy.signedInAs}
                </span>
                <span className="block truncate text-sm font-bold text-foreground">
                  {profile?.companyName || displayName || status.user.email}
                  {(profile?.companyName || displayName) && (
                    <span className="font-normal text-muted-foreground">
                      {" "}
                      · {status.user.email}
                    </span>
                  )}
                </span>
              </span>
            </div>
          </div>
          <Button variant="outline" onClick={handleLogout} disabled={loggingOut}>
            <LogOut className="mr-2 size-4" />
            {loggingOut ? copy.loggingOut : copy.logout}
          </Button>
        </div>

        <div className="mt-8">
          <InterestReturnNotice next={next} />
        </div>
        <div className="mt-6">
          <JobNotifications />
        </div>
        <section className="mt-10 grid gap-6 lg:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.75fr)]">
          <div className="space-y-6">
            <StatusCard
              providerStatus={providerStatus}
              title={copy.status[providerStatus]}
              text={
                isAgent ? copy.agentStatusText[providerStatus] : copy.statusText[providerStatus]
              }
              accessGranted={status.canExpressInterest}
              accessGrantedLabel={copy.accessGranted}
              accessLockedLabel={isAgent ? copy.constructionAccessNotApplicable : copy.accessLocked}
            />

            {status.billing.billingEnabled || billingOverview?.invoices.length ? (
              <BillingCard
                access={status.billing}
                overview={billingOverview}
                language={language}
                role={isAgent ? "immobilienmakler" : "auftragnehmer"}
                defaultCustomerName={displayName || profile?.companyName || status.user.email}
                defaultCompany={profile?.companyName || ""}
                onOverviewChange={setBillingOverview}
              />
            ) : null}

            <Card className="border-border/70 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="flex items-center gap-2 font-display text-xl">
                    <UserRound className="size-5 text-primary" />
                    {copy.profile}
                  </CardTitle>
                  <ProfileEditDialog
                    firstName={profile?.firstName ?? ""}
                    lastName={profile?.lastName ?? ""}
                    companyName={profile?.companyName ?? ""}
                    phone={profile?.phone ?? ""}
                    isProvider
                    companyNameLocked={providerStatus === "approved"}
                  />
                </div>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-2">
                <AccountField
                  icon={<UserRound />}
                  label={copy.name}
                  value={displayName || copy.notProvided}
                />
                <AccountField
                  icon={<Building2 />}
                  label={copy.company}
                  value={profile?.companyName || copy.notProvided}
                />
                <AccountField icon={<Mail />} label={copy.email} value={status.user.email} />
                <AccountField
                  icon={<Phone />}
                  label={copy.phone}
                  value={profile?.phone || copy.notProvided}
                />
                <AccountField
                  icon={<Building2 />}
                  label={copy.accountType}
                  value={profile ? copy.accountTypeLabels[profile.role] : copy.notProvided}
                />
              </CardContent>
            </Card>

            {!isAgent ? <ProviderFeedbackSection /> : null}
          </div>

          <div className="space-y-6">
            <Card className="overflow-hidden border-border/70 shadow-sm">
              <div className="h-1.5 bg-gradient-brand" />
              <CardHeader className="pb-4">
                <CardTitle className="font-display text-xl">{copy.verification}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <VerificationRow
                  icon={<Mail />}
                  label={copy.emailVerified}
                  value={status.user.emailConfirmed ? copy.emailVerified : copy.status.pending}
                  positive={status.user.emailConfirmed}
                />
                <VerificationRow
                  icon={<BadgeCheck />}
                  label={copy.providerStatus}
                  value={copy.status[providerStatus]}
                  positive={providerStatus === "approved"}
                />
                <VerificationRow
                  icon={<KeyRound />}
                  label={copy.membership}
                  value={
                    status.billing.billingEnabled && status.billing.planCode
                      ? billingPlanName(status.billing.planCode, language)
                      : copy.membershipStatus[profile?.membershipStatus ?? "free"]
                  }
                  positive={
                    status.billing.billingEnabled
                      ? status.billing.subscriptionStatus === "active" ||
                        status.billing.subscriptionStatus === "grace"
                      : profile?.membershipStatus === "active" ||
                        profile?.membershipStatus === "free"
                  }
                />
                <VerificationRow
                  icon={<LockKeyhole />}
                  label={copy.contactAccess}
                  value={
                    isAgent
                      ? copy.constructionAccessNotApplicable
                      : status.canExpressInterest
                        ? copy.accessGranted
                        : copy.accessLocked
                  }
                  positive={status.canExpressInterest}
                />
              </CardContent>
            </Card>

            <Card className="border-primary/15 bg-brand-soft/60 shadow-sm">
              <CardContent className="p-6">
                <ShieldCheck className="size-6 text-primary" />
                <h2 className="mt-4 font-display text-lg font-bold">{copy.helpTitle}</h2>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{copy.helpText}</p>
                {providerStatus === "pending" ? (
                  <div className="mt-5 rounded-xl border border-primary/15 bg-background/75 p-4">
                    <p className="text-sm leading-6 text-muted-foreground">
                      {copy.completeProfileText}
                    </p>
                    <Button asChild className="mt-4 w-full">
                      <a
                        href={`/registrieren?typ=${isAgent ? "immobilienmakler" : "auftragnehmer"}${next ? `&next=${encodeURIComponent(next)}` : ""}`}
                      >
                        {copy.completeProfile}
                      </a>
                    </Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <Button asChild size="lg" className="h-12 w-full shadow-brand">
              <a href={isAgent ? "/immobilien" : "/auftraege"}>
                {isAgent ? copy.browseProperties : copy.browseJobs}
                <ArrowRight className="ml-2 size-4" />
              </a>
            </Button>
          </div>
        </section>
        {!isAgent ? (
          <div className="mt-10">
            <CompanyInterests />
          </div>
        ) : null}
        <div className="mt-10">
          <OwnJobsSection />
        </div>
      </div>
    </div>
  );
}

const billingAccountCopy = {
  de: {
    title: "Mitgliedschaft & Rechnungen",
    active: "Aktiv",
    lifetime: "Lifetime-Pass – dauerhaft freigeschaltet",
    cancel: "Zum Periodenende kündigen",
    resume: "Kündigung zurücknehmen",
    cancelTitle: "Abo zum Periodenende kündigen?",
    cancelText:
      "Der Zugriff bleibt bis zum bereits bezahlten Datum aktiv. Danach entstehen keine neuen Rechnungen.",
    cancellationScheduled: "Kündigung zum Periodenende vorgemerkt.",
    cancellationUpdated: "Abo-Einstellung wurde aktualisiert.",
    creditPdf: "Gutschrift PDF",
    noPlan: "Keine aktive Mitgliedschaft",
    noPlanText: "Wählen Sie das Angebot, das zu Ihrem Unternehmen passt.",
    validUntil: "Bezahlt bis",
    invoices: "Letzte Rechnungen",
    period: "Leistungszeitraum",
    invoicePending: "Rechnung wird vorbereitet",
    choose: "Angebote ansehen",
    order: "Rechnung bestellen",
    upgrade: "Auf Pro wechseln",
    upgradeTitle: "Pro per Rechnung bestellen",
    upgradeText:
      "Pro wird nach bestätigtem Zahlungseingang freigeschaltet. Ihre bereits bezahlte restliche Verzeichnis-Laufzeit bleibt erhalten; die neue Pro-Periode wird daran angehängt. Bis dahin können Sie noch kein Interesse bekunden. Es gibt keine anteilige Geldgutschrift.",
    replaceDirectoryInvoice:
      "Eine noch unbezahlte Verzeichnis-Rechnung wird beim Wechsel durch die Pro-Rechnung ersetzt. Bei einer bereits verbuchten Teilzahlung ist zuerst eine Klärung mit Begner nötig.",
    orderTitle: "Mitgliedschaft per Rechnung bestellen",
    orderText: "Wir prüfen die Angaben und stellen anschliessend Ihre Schweizer QR-Rechnung aus.",
    customerName: "Kontaktperson",
    company: "Firma (optional)",
    street: "Strasse",
    buildingNumber: "Nr.",
    postalCode: "PLZ",
    city: "Ort",
    countryCode: "Land",
    submitOrder: "Verbindlich bestellen",
    requestSuccess: "Rechnungsanfrage wurde übermittelt.",
    paymentTitle: "Zahlungsangaben",
    bankTransfer: "Banküberweisung / QR-Rechnung",
    iban: "IBAN",
    reference: "Referenz",
    due: "Zahlbar bis",
    twintLater: "TWINT folgt später und ist aktuell nicht aktiv.",
    pdf: "Rechnung PDF",
    loading: "Abrechnung wird geladen.",
    status: {
      draft: "In Vorbereitung",
      open: "Offen",
      paid: "Bezahlt",
      overdue: "Überfällig",
      void: "Storniert",
      refunded: "Erstattet",
    },
  },
  fr: {
    title: "Abonnement et factures",
    active: "Actif",
    lifetime: "Pass à vie – accès permanent",
    cancel: "Résilier à la fin de la période",
    resume: "Annuler la résiliation",
    cancelTitle: "Résilier à la fin de la période ?",
    cancelText:
      "L’accès reste actif jusqu’à la date déjà payée. Aucune nouvelle facture ne sera ensuite créée.",
    cancellationScheduled: "Résiliation programmée à la fin de la période.",
    cancellationUpdated: "Le paramètre d’abonnement a été actualisé.",
    creditPdf: "Avoir PDF",
    noPlan: "Aucun abonnement actif",
    noPlanText: "Choisissez l’offre adaptée à votre entreprise.",
    validUntil: "Payé jusqu’au",
    invoices: "Dernières factures",
    period: "Période de prestations",
    invoicePending: "Facture en préparation",
    choose: "Voir les offres",
    order: "Commander une facture",
    upgrade: "Passer à Pro",
    upgradeTitle: "Commander Pro sur facture",
    upgradeText:
      "Pro est activé après confirmation du paiement. La durée restante déjà payée pour l’annuaire est conservée ; la nouvelle période Pro s’y ajoute. Jusque-là, vous ne pouvez pas manifester votre intérêt. Aucun avoir au prorata n’est émis.",
    replaceDirectoryInvoice:
      "Une facture d’annuaire encore impayée est remplacée par la facture Pro lors du changement. En cas de paiement partiel déjà enregistré, contactez d’abord Begner.",
    orderTitle: "Commander l’abonnement sur facture",
    orderText: "Nous vérifions les données avant d’établir votre facture QR suisse.",
    customerName: "Personne de contact",
    company: "Entreprise (facultatif)",
    street: "Rue",
    buildingNumber: "No",
    postalCode: "NPA",
    city: "Localité",
    countryCode: "Pays",
    submitOrder: "Commander avec obligation de paiement",
    requestSuccess: "La demande de facture a été transmise.",
    paymentTitle: "Données de paiement",
    bankTransfer: "Virement bancaire / facture QR",
    iban: "IBAN",
    reference: "Référence",
    due: "Payable jusqu’au",
    twintLater: "TWINT suivra ultérieurement et n’est pas encore actif.",
    pdf: "Facture PDF",
    loading: "Chargement de la facturation.",
    status: {
      draft: "En préparation",
      open: "Ouverte",
      paid: "Payée",
      overdue: "En retard",
      void: "Annulée",
      refunded: "Remboursée",
    },
  },
  it: {
    title: "Abbonamento e fatture",
    active: "Attivo",
    lifetime: "Pass a vita – accesso permanente",
    cancel: "Disdire alla fine del periodo",
    resume: "Revocare la disdetta",
    cancelTitle: "Disdire alla fine del periodo?",
    cancelText:
      "L’accesso resta attivo fino alla data già pagata. In seguito non verranno create nuove fatture.",
    cancellationScheduled: "Disdetta programmata alla fine del periodo.",
    cancellationUpdated: "L’impostazione dell’abbonamento è stata aggiornata.",
    creditPdf: "Nota di credito PDF",
    noPlan: "Nessun abbonamento attivo",
    noPlanText: "Scegliete l’offerta adatta alla vostra impresa.",
    validUntil: "Pagato fino al",
    invoices: "Ultime fatture",
    period: "Periodo di prestazione",
    invoicePending: "Fattura in preparazione",
    choose: "Vedi offerte",
    order: "Ordinare la fattura",
    upgrade: "Passare a Pro",
    upgradeTitle: "Ordinare Pro con fattura",
    upgradeText:
      "Pro viene attivato dopo la conferma del pagamento. La durata residua già pagata per l’elenco viene mantenuta; il nuovo periodo Pro si aggiunge a questa. Fino ad allora non è possibile manifestare interesse. Non viene emessa una nota di credito proporzionale.",
    replaceDirectoryInvoice:
      "Una fattura dell’elenco ancora non pagata viene sostituita dalla fattura Pro al momento del cambio. Se è già stato registrato un pagamento parziale, contattate prima Begner.",
    orderTitle: "Ordinare l’abbonamento con fattura",
    orderText: "Verifichiamo i dati prima di emettere la fattura QR svizzera.",
    customerName: "Persona di contatto",
    company: "Azienda (facoltativo)",
    street: "Via",
    buildingNumber: "N.",
    postalCode: "NPA",
    city: "Località",
    countryCode: "Paese",
    submitOrder: "Ordinare con obbligo di pagamento",
    requestSuccess: "La richiesta di fattura è stata trasmessa.",
    paymentTitle: "Dati di pagamento",
    bankTransfer: "Bonifico bancario / fattura QR",
    iban: "IBAN",
    reference: "Riferimento",
    due: "Pagabile entro",
    twintLater: "TWINT sarà aggiunto in seguito e non è ancora attivo.",
    pdf: "Fattura PDF",
    loading: "Caricamento fatturazione.",
    status: {
      draft: "In preparazione",
      open: "Aperta",
      paid: "Pagata",
      overdue: "Scaduta",
      void: "Annullata",
      refunded: "Rimborsata",
    },
  },
  en: {
    title: "Membership & invoices",
    active: "Active",
    lifetime: "Lifetime pass – permanently enabled",
    cancel: "Cancel at period end",
    resume: "Revoke cancellation",
    cancelTitle: "Cancel at the end of the period?",
    cancelText:
      "Access remains active until the paid-through date. No further invoices will be created afterwards.",
    cancellationScheduled: "Cancellation scheduled for the end of the period.",
    cancellationUpdated: "The subscription setting was updated.",
    creditPdf: "Credit note PDF",
    noPlan: "No active membership",
    noPlanText: "Choose the plan that fits your business.",
    validUntil: "Paid until",
    invoices: "Latest invoices",
    period: "Service period",
    invoicePending: "Invoice being prepared",
    choose: "View plans",
    order: "Order invoice",
    upgrade: "Upgrade to Pro",
    upgradeTitle: "Order Pro by invoice",
    upgradeText:
      "Pro is activated once payment is confirmed. Your remaining paid directory time is preserved, and the new Pro period is added afterwards. Until then, you cannot express interest. No prorated cash credit is issued.",
    replaceDirectoryInvoice:
      "An unpaid directory invoice is replaced by the Pro invoice when you upgrade. If a partial payment has already been recorded, contact Begner first.",
    orderTitle: "Order membership by invoice",
    orderText: "We verify the details before issuing your Swiss QR-bill.",
    customerName: "Contact person",
    company: "Company (optional)",
    street: "Street",
    buildingNumber: "No.",
    postalCode: "Postal code",
    city: "City",
    countryCode: "Country",
    submitOrder: "Place binding order",
    requestSuccess: "The invoice request was submitted.",
    paymentTitle: "Payment details",
    bankTransfer: "Bank transfer / QR-bill",
    iban: "IBAN",
    reference: "Reference",
    due: "Payable by",
    twintLater: "TWINT will follow later and is not active yet.",
    pdf: "Invoice PDF",
    loading: "Loading billing.",
    status: {
      draft: "Being prepared",
      open: "Open",
      paid: "Paid",
      overdue: "Overdue",
      void: "Cancelled",
      refunded: "Refunded",
    },
  },
} as const;

function BillingCard({
  access,
  overview,
  language,
  role,
  defaultCustomerName,
  defaultCompany,
  onOverviewChange,
}: {
  access: BillingAccess;
  overview: BillingOverview | null;
  language: LanguageCode;
  role: ProviderAccountRole;
  defaultCustomerName: string;
  defaultCompany: string;
  onOverviewChange: (overview: BillingOverview) => void;
}) {
  const requestInvoice = useServerFn(requestOwnBillingInvoice);
  const getDiscounts = useServerFn(getOwnDiscounts);
  const applyDiscount = useServerFn(applySubscriptionDiscount);
  const [discounts, setDiscounts] = useState<OwnDiscount[]>([]);
  const [discount, setDiscount] = useState<DiscountSelection | null>(null);
  const [renewalDiscount, setRenewalDiscount] = useState<DiscountSelection | null>(null);
  useEffect(() => {
    void getDiscounts()
      .then(setDiscounts)
      .catch(() => {});
  }, [getDiscounts]);
  const updateCancellation = useServerFn(setOwnBillingCancellation);
  const copy = billingAccountCopy[language];
  const locale = language === "de" ? "de-CH" : `${language}-CH`;
  const planCode = access.planCode ?? overview?.subscription?.planCode;
  const periodEnd =
    access.accessSource === "lifetime"
      ? null
      : (access.currentPeriodEnd ?? overview?.subscription?.currentPeriodEnd);
  const canUpgrade =
    role === "auftragnehmer" &&
    access.planCode === "contractor_directory" &&
    access.directoryAccess &&
    !access.jobContactAccess;
  const eligiblePlanCodes =
    role === "immobilienmakler"
      ? (["agent_membership"] as const)
      : canUpgrade
        ? (["contractor_pro"] as const)
        : (["contractor_directory", "contractor_pro"] as const);
  const [orderOpen, setOrderOpen] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [updatingCancellation, setUpdatingCancellation] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<(typeof eligiblePlanCodes)[number]>(
    role === "immobilienmakler" ? "agent_membership" : "contractor_pro",
  );
  const [customerName, setCustomerName] = useState(defaultCustomerName);
  const [company, setCompany] = useState(defaultCompany);
  const [street, setStreet] = useState("");
  const [buildingNumber, setBuildingNumber] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [countryCode, setCountryCode] = useState("CH");
  const active = access.directoryAccess || access.jobContactAccess;
  const pendingInvoice = overview?.invoices.find(
    (invoice) =>
      invoice.status === "draft" || invoice.status === "open" || invoice.status === "overdue",
  );
  const payableInvoice = overview?.invoices.find(
    (invoice) => invoice.status === "open" || invoice.status === "overdue",
  );
  const subscription = overview?.subscription;
  const isLifetime = access.accessSource === "lifetime";

  async function handleCancellation(cancelAtPeriodEnd: boolean) {
    if (!overview?.subscription) return;
    setUpdatingCancellation(true);
    try {
      const result = await updateCancellation({ data: { cancelAtPeriodEnd } });
      onOverviewChange({ ...overview, subscription: result.subscription });
      toast.success(cancelAtPeriodEnd ? copy.cancellationScheduled : copy.cancellationUpdated);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Das Abo konnte nicht aktualisiert werden.",
      );
    } finally {
      setUpdatingCancellation(false);
    }
  }

  async function handleInvoiceRequest(event: FormEvent) {
    event.preventDefault();
    if (!overview) return;
    setOrdering(true);
    try {
      const result = await requestInvoice({
        data: {
          planCode: selectedPlan,
          discountCode: discount?.code,
          expectedAmount: discount?.expectedAmount,
          customerName,
          customerCompany: company || undefined,
          billingAddress: { street, buildingNumber, postalCode, city, countryCode },
        },
      });
      onOverviewChange(result.overview);
      setOrderOpen(false);
      toast.success(copy.requestSuccess);
    } catch (error) {
      toast.error(
        error instanceof Error && error.message.startsWith("discount_")
          ? discountError(error.message, language)
          : error instanceof Error
            ? error.message
            : "Die Bestellung ist fehlgeschlagen.",
      );
    } finally {
      setOrdering(false);
    }
  }

  return (
    <>
      <Card id="billing" className="scroll-mt-24 overflow-hidden border-border/70 shadow-sm">
        <div className="h-1.5 bg-gradient-brand" />
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 font-display text-xl">
            <ReceiptText className="size-5 text-primary" />
            {copy.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {!overview ? (
            <p className="text-sm text-muted-foreground">{copy.loading}</p>
          ) : planCode && active ? (
            <div className="flex flex-col justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/70 p-4 sm:flex-row sm:items-center">
              <div>
                <p className="font-bold text-foreground">{billingPlanName(planCode, language)}</p>
                {periodEnd ? (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {copy.validUntil} {new Intl.DateTimeFormat(locale).format(new Date(periodEnd))}
                  </p>
                ) : null}
                {isLifetime ? (
                  <p className="mt-1 text-sm font-medium text-emerald-800">{copy.lifetime}</p>
                ) : null}
              </div>
              <Badge className="w-fit bg-emerald-600 hover:bg-emerald-600">{copy.active}</Badge>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border bg-muted/35 p-4">
              <p className="font-bold">{copy.noPlan}</p>
              <p className="mt-1 text-sm text-muted-foreground">{copy.noPlanText}</p>
            </div>
          )}

          {subscription && !isLifetime ? (
            <div className="flex flex-col gap-3 rounded-xl border border-border/70 p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                {subscription.cancelAtPeriodEnd ? copy.cancellationScheduled : copy.cancelText}
              </p>
              {subscription.cancelAtPeriodEnd ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={updatingCancellation}
                  onClick={() => void handleCancellation(false)}
                >
                  {copy.resume}
                </Button>
              ) : (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="outline" size="sm">
                      {copy.cancel}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{copy.cancelTitle}</AlertDialogTitle>
                      <AlertDialogDescription>{copy.cancelText}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                      <AlertDialogAction
                        disabled={updatingCancellation}
                        onClick={() => void handleCancellation(true)}
                      >
                        {copy.cancel}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          ) : null}

          {payableInvoice && overview?.settings ? (
            <div className="rounded-xl border border-primary/20 bg-brand-soft/45 p-4">
              <div className="flex items-center gap-2 font-bold">
                <Landmark className="size-4 text-primary" /> {copy.paymentTitle}
              </div>
              <p className="mt-2 text-sm font-medium">{copy.bankTransfer}</p>
              <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-[90px_minmax(0,1fr)]">
                <dt className="text-muted-foreground">{copy.iban}</dt>
                <dd className="break-all font-mono font-semibold">{overview.settings.iban}</dd>
                <dt className="text-muted-foreground">{copy.reference}</dt>
                <dd className="break-all font-mono font-semibold">
                  {payableInvoice.paymentReference}
                </dd>
                {payableInvoice.dueAt ? (
                  <>
                    <dt className="text-muted-foreground">{copy.due}</dt>
                    <dd>
                      {new Intl.DateTimeFormat(locale).format(new Date(payableInvoice.dueAt))}
                    </dd>
                  </>
                ) : null}
              </dl>
              <p className="mt-3 text-xs text-muted-foreground">{copy.twintLater}</p>
            </div>
          ) : null}

          {overview?.invoices.length ? (
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
                {copy.invoices}
              </p>
              <div className="mt-3 divide-y divide-border rounded-xl border border-border/70">
                {overview.invoices.slice(0, 3).map((invoice) => (
                  <div
                    key={invoice.id}
                    className="flex items-center justify-between gap-3 px-3.5 py-3 text-sm"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-semibold">
                        {invoice.invoiceNumber || copy.invoicePending}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {invoice.settlementKind === "discount"
                          ? discountCopy[language].settled
                          : copy.status[invoice.status]}
                      </p>
                      {invoice.discountMinor > 0 ? (
                        <p className="text-xs text-muted-foreground">
                          {invoice.discountCode ?? discountCopy[language].current}: −
                          {formatBillingPrice(invoice.discountMinor, locale)} ·{" "}
                          {formatBillingPrice(invoice.subtotalMinor, locale)}
                        </p>
                      ) : null}
                      {invoice.servicePeriodStart && invoice.servicePeriodEnd ? (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {copy.period}:{" "}
                          {formatBillingPeriod(
                            invoice.servicePeriodStart,
                            invoice.servicePeriodEnd,
                            locale,
                          )}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="font-bold">
                        {formatBillingPrice(invoice.amountMinor, locale, invoice.currency)}
                      </span>
                      {invoice.invoiceNumber && overview.settings ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          aria-label={copy.pdf}
                          onClick={() =>
                            void downloadBillingInvoicePdf(
                              invoice,
                              overview.settings!,
                              billingPlanName(invoice.planCode, language),
                            ).catch((error) =>
                              toast.error(
                                error instanceof Error
                                  ? error.message
                                  : "Das PDF konnte nicht erstellt werden.",
                              ),
                            )
                          }
                        >
                          <Download className="size-4" />
                        </Button>
                      ) : null}
                      {invoice.creditNoteNumber && overview.settings ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          aria-label={copy.creditPdf}
                          onClick={() =>
                            void downloadBillingCreditNotePdf(
                              invoice,
                              overview.settings!,
                              billingPlanName(invoice.planCode, language),
                            ).catch((error) =>
                              toast.error(
                                error instanceof Error
                                  ? error.message
                                  : "Die Gutschrift konnte nicht erstellt werden.",
                              ),
                            )
                          }
                        >
                          <Download className="size-4 text-destructive" />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="grid gap-2 sm:grid-cols-2">
            <Button asChild variant="outline" className="w-full">
              <Link
                to="/anbieter-preise"
                search={{ kategorie: role === "immobilienmakler" ? "makler" : "auftragnehmer" }}
              >
                {copy.choose}
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
            {overview?.settings?.billingEnabled ? (
              <Button
                type="button"
                className="w-full"
                disabled={
                  !overview ||
                  (Boolean(pendingInvoice) &&
                    !(canUpgrade && pendingInvoice?.planCode === "contractor_directory")) ||
                  (active && !canUpgrade)
                }
                onClick={() => {
                  if (canUpgrade) setSelectedPlan("contractor_pro");
                  setOrderOpen(true);
                }}
              >
                {canUpgrade ? copy.upgrade : copy.order}
                <ReceiptText className="ml-2 size-4" />
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {discounts.map((item) => (
        <div key={item.code} className="rounded-xl border bg-brand-soft/30 p-4 text-sm">
          <strong>
            {discountCopy[language].current}: {item.code} · {item.percent} %
          </strong>
          <p>
            {item.startsOn ? `${discountCopy[language].from} ${item.startsOn}` : ""}{" "}
            {item.endsOn
              ? `${discountCopy[language].until} ${new Intl.DateTimeFormat(locale, { timeZone: "UTC" }).format(new Date(new Date(`${item.endsOn}T12:00:00Z`).getTime() - 86400000))}`
              : ""}
          </p>
        </div>
      ))}
      {active && !isLifetime && planCode && !pendingInvoice && !subscription?.cancelAtPeriodEnd ? (
        <div className="space-y-3">
          <DiscountCodeInput
            planCode={planCode}
            language={language}
            onChange={setRenewalDiscount}
            disabled={ordering}
          />
          <Button
            disabled={!renewalDiscount || ordering}
            onClick={async () => {
              if (!renewalDiscount) return;
              setOrdering(true);
              try {
                await applyDiscount({
                  data: {
                    code: renewalDiscount.code,
                    expectedAmount: renewalDiscount.expectedAmount,
                  },
                });
                setDiscounts(await getDiscounts());
                setRenewalDiscount(null);
                toast.success(discountCopy[language].saved);
              } catch (error) {
                toast.error(discountError(error instanceof Error ? error.message : "", language));
              } finally {
                setOrdering(false);
              }
            }}
          >
            {discountCopy[language].apply}
          </Button>
        </div>
      ) : null}

      <Dialog open={orderOpen} onOpenChange={setOrderOpen}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{canUpgrade ? copy.upgradeTitle : copy.orderTitle}</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {canUpgrade ? copy.upgradeText : copy.orderText}
            </p>
            {canUpgrade && pendingInvoice?.planCode === "contractor_directory" ? (
              <p className="text-sm text-muted-foreground">{copy.replaceDirectoryInvoice}</p>
            ) : null}
          </DialogHeader>
          <form className="space-y-5" onSubmit={handleInvoiceRequest}>
            <div className="grid gap-3 sm:grid-cols-2">
              {eligiblePlanCodes.map((code) => {
                const plan = fallbackBillingPlans.find((item) => item.code === code)!;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setSelectedPlan(code)}
                    className={cn(
                      "rounded-xl border p-4 text-left transition",
                      selectedPlan === code
                        ? "border-primary bg-brand-soft/55 ring-1 ring-primary"
                        : "border-border hover:border-primary/40",
                    )}
                  >
                    <span className="block font-bold">{billingPlanName(code, language)}</span>
                    <span className="mt-1 block text-sm text-muted-foreground">
                      {formatBillingPrice(plan.priceMinor, locale, plan.currency)} / Monat
                    </span>
                  </button>
                );
              })}
            </div>
            <DiscountCodeInput
              planCode={selectedPlan}
              language={language}
              onChange={setDiscount}
              disabled={ordering}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="billing-customer">{copy.customerName} *</Label>
                <Input
                  id="billing-customer"
                  value={customerName}
                  onChange={(event) => setCustomerName(event.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billing-company">{copy.company}</Label>
                <Input
                  id="billing-company"
                  value={company}
                  onChange={(event) => setCompany(event.target.value)}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="billing-street">{copy.street} *</Label>
                <div className="grid grid-cols-[minmax(0,1fr)_90px] gap-3">
                  <Input
                    id="billing-street"
                    value={street}
                    onChange={(event) => setStreet(event.target.value)}
                    required
                  />
                  <div>
                    <Label htmlFor="billing-number" className="sr-only">
                      {copy.buildingNumber}
                    </Label>
                    <Input
                      id="billing-number"
                      aria-label={copy.buildingNumber}
                      placeholder={copy.buildingNumber}
                      value={buildingNumber}
                      onChange={(event) => setBuildingNumber(event.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="billing-postal">{copy.postalCode} *</Label>
                <Input
                  id="billing-postal"
                  value={postalCode}
                  onChange={(event) => setPostalCode(event.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billing-city">{copy.city} *</Label>
                <Input
                  id="billing-city"
                  value={city}
                  onChange={(event) => setCity(event.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billing-country">{copy.countryCode} *</Label>
                <Input
                  id="billing-country"
                  value={countryCode}
                  onChange={(event) => setCountryCode(event.target.value.toUpperCase())}
                  minLength={2}
                  maxLength={2}
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOrderOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={ordering}>
                {copy.submitOrder}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

const ownJobsCopy = {
  de: {
    customerTitle: "Ihre Aufträge auf einen Blick",
    customerIntro:
      "Hier sehen Sie alle Ausschreibungen, die mit Ihrem Begner-Konto erstellt wurden.",
    title: "Meine Ausschreibungen",
    empty: "Sie haben mit diesem Konto noch keinen Auftrag eingestellt.",
    post: "Neue Anfrage eingeben",
    reference: "Referenz",
    created: "Eingereicht",
    view: "Öffentliches Inserat ansehen",
    status: {
      neu: "Eingereicht",
      in_bearbeitung: "In Prüfung",
      freigegeben: "Freigegeben",
      abgelehnt: "Abgelehnt",
      published: "Online",
      hidden: "Nicht öffentlich",
      closed: "Vergeben",
      expired: "Abgelaufen",
      deleted: "Gelöscht",
    },
    loadError: "Die eigenen Aufträge konnten nicht geladen werden.",
  },
  fr: {
    customerTitle: "Vos mandats en un coup d’œil",
    customerIntro: "Retrouvez ici toutes les demandes créées avec votre compte Begner.",
    title: "Mes demandes",
    empty: "Vous n’avez pas encore publié de mandat avec ce compte.",
    post: "Publier un mandat",
    reference: "Référence",
    created: "Envoyé",
    view: "Voir l’annonce publique",
    status: {
      neu: "Envoyé",
      in_bearbeitung: "En vérification",
      freigegeben: "Validé",
      abgelehnt: "Refusé",
      published: "En ligne",
      hidden: "Non public",
      closed: "Attribué",
      expired: "Expiré",
      deleted: "Supprimé",
    },
    loadError: "Vos mandats n’ont pas pu être chargés.",
  },
  it: {
    customerTitle: "I suoi incarichi in sintesi",
    customerIntro: "Qui trova tutte le richieste create con il suo account Begner.",
    title: "I miei incarichi",
    empty: "Non ha ancora pubblicato un incarico con questo account.",
    post: "Pubblica un incarico",
    reference: "Riferimento",
    created: "Inviato",
    view: "Vedi annuncio pubblico",
    status: {
      neu: "Inviato",
      in_bearbeitung: "In verifica",
      freigegeben: "Approvato",
      abgelehnt: "Rifiutato",
      published: "Online",
      hidden: "Non pubblico",
      closed: "Assegnato",
      expired: "Scaduto",
      deleted: "Eliminato",
    },
    loadError: "Non è stato possibile caricare gli incarichi.",
  },
  en: {
    customerTitle: "Your jobs at a glance",
    customerIntro: "See every request created with your Begner account here.",
    title: "My job requests",
    empty: "You have not posted a job with this account yet.",
    post: "Post a new job",
    reference: "Reference",
    created: "Submitted",
    view: "View public listing",
    status: {
      neu: "Submitted",
      in_bearbeitung: "Under review",
      freigegeben: "Approved",
      abgelehnt: "Rejected",
      published: "Online",
      hidden: "Not public",
      closed: "Awarded",
      expired: "Expired",
      deleted: "Deleted",
    },
    loadError: "Your jobs could not be loaded.",
  },
} as const;

function CustomerAccountView({
  email,
  firstName,
  lastName,
  phone,
  loggingOut,
  onLogout,
}: {
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  loggingOut: boolean;
  onLogout: () => Promise<void>;
}) {
  const { language } = useLanguage();
  const copy = authCopy[language].account;
  const jobsCopy = ownJobsCopy[language];
  const displayName = [firstName, lastName].filter(Boolean).join(" ");
  return (
    <main className="relative isolate overflow-hidden bg-muted/25 py-9 sm:py-20">
      <div className="pointer-events-none absolute left-1/2 top-0 -z-10 h-80 w-[70rem] -translate-x-1/2 rounded-full bg-brand-soft/80 blur-3xl" />
      <div className="container-page">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-background/80 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-brand-deep shadow-sm">
              <UserRound className="size-4" />
              {copy.eyebrow}
            </div>
            <h1 className="mt-5 max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-5xl">
              {jobsCopy.customerTitle}
            </h1>
            <p className="mt-3 max-w-2xl text-lg text-muted-foreground">{jobsCopy.customerIntro}</p>
          </div>
          <Button variant="outline" onClick={() => void onLogout()} disabled={loggingOut}>
            <LogOut className="size-4" />
            {loggingOut ? copy.loggingOut : copy.logout}
          </Button>
        </div>

        <Card className="mt-10 border-border/70 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-2 font-display text-xl">
                <UserRound className="size-5 text-primary" />
                {copy.profile}
              </CardTitle>
              <ProfileEditDialog
                firstName={firstName ?? ""}
                lastName={lastName ?? ""}
                companyName=""
                phone={phone ?? ""}
                isProvider={false}
              />
            </div>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <AccountField
              icon={<UserRound />}
              label={copy.name}
              value={displayName || copy.notProvided}
            />
            <AccountField icon={<Mail />} label={copy.email} value={email} />
            <AccountField icon={<Phone />} label={copy.phone} value={phone || copy.notProvided} />
            <AccountField
              icon={<BadgeCheck />}
              label={copy.accountType}
              value={copy.accountTypeLabels.kunde}
            />
          </CardContent>
        </Card>

        <div className="mt-10">
          <JobNotifications />
        </div>
        <div className="mt-10">
          <OwnJobsSection />
        </div>
      </div>
    </main>
  );
}

const accountEditCopy = {
  de: {
    edit: "Profil bearbeiten",
    title: "Profil bearbeiten",
    intro: "Aktualisieren Sie Ihre persönlichen Angaben und Kontaktdaten.",
    firstName: "Vorname",
    lastName: "Nachname",
    company: "Firma",
    phone: "Telefon",
    cancel: "Abbrechen",
    save: "Änderungen speichern",
    saving: "Wird gespeichert …",
    saved: "Profil wurde aktualisiert.",
    error: "Profil konnte nicht gespeichert werden.",
  },
  fr: {
    edit: "Modifier le profil",
    title: "Modifier le profil",
    intro: "Mettez à jour vos informations personnelles et vos coordonnées.",
    firstName: "Prénom",
    lastName: "Nom",
    company: "Entreprise",
    phone: "Téléphone",
    cancel: "Annuler",
    save: "Enregistrer",
    saving: "Enregistrement …",
    saved: "Profil mis à jour.",
    error: "Le profil n’a pas pu être enregistré.",
  },
  it: {
    edit: "Modifica profilo",
    title: "Modifica profilo",
    intro: "Aggiornate i dati personali e di contatto.",
    firstName: "Nome",
    lastName: "Cognome",
    company: "Azienda",
    phone: "Telefono",
    cancel: "Annulla",
    save: "Salva modifiche",
    saving: "Salvataggio …",
    saved: "Profilo aggiornato.",
    error: "Non è stato possibile salvare il profilo.",
  },
  en: {
    edit: "Edit profile",
    title: "Edit profile",
    intro: "Update your personal and contact details.",
    firstName: "First name",
    lastName: "Last name",
    company: "Company",
    phone: "Phone",
    cancel: "Cancel",
    save: "Save changes",
    saving: "Saving …",
    saved: "Profile updated.",
    error: "The profile could not be saved.",
  },
} as const;

function ProfileEditDialog({
  firstName,
  lastName,
  companyName,
  phone,
  isProvider,
  companyNameLocked = false,
}: {
  firstName: string;
  lastName: string;
  companyName: string;
  phone: string;
  isProvider: boolean;
  companyNameLocked?: boolean;
}) {
  const { language } = useLanguage();
  const copy = accountEditCopy[language];
  const saveProfile = useServerFn(updateOwnAccountProfile);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({ firstName, lastName, companyName, phone });

  useEffect(
    () => setValues({ firstName, lastName, companyName, phone }),
    [firstName, lastName, companyName, phone],
  );

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await saveProfile({ data: values });
      toast.success(copy.saved);
      setOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : copy.error);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Pencil className="mr-1.5 size-3.5" />
        {copy.edit}
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <p className="text-sm text-muted-foreground">{copy.intro}</p>
        </DialogHeader>
        <form className="space-y-4" onSubmit={save}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="profile-first-name">{copy.firstName}</Label>
              <Input
                id="profile-first-name"
                required
                value={values.firstName}
                onChange={(event) =>
                  setValues((current) => ({ ...current, firstName: event.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-last-name">{copy.lastName}</Label>
              <Input
                id="profile-last-name"
                required
                value={values.lastName}
                onChange={(event) =>
                  setValues((current) => ({ ...current, lastName: event.target.value }))
                }
              />
            </div>
          </div>
          {isProvider ? (
            <div className="space-y-2">
              <Label htmlFor="profile-company">{copy.company}</Label>
              <Input
                id="profile-company"
                required
                readOnly={companyNameLocked}
                aria-describedby={companyNameLocked ? "profile-company-locked" : undefined}
                value={values.companyName}
                onChange={(event) =>
                  setValues((current) => ({ ...current, companyName: event.target.value }))
                }
              />
              {companyNameLocked ? (
                <p id="profile-company-locked" className="text-xs text-muted-foreground">
                  {
                    {
                      de: "Änderungen des geprüften Firmennamens übernimmt Begner: info@begner.ch.",
                      fr: "Begner peut modifier le nom vérifié de l’entreprise: info@begner.ch.",
                      it: "Begner può modificare il nome verificato dell’impresa: info@begner.ch.",
                      en: "Begner can update your verified company name: info@begner.ch.",
                    }[language]
                  }
                </p>
              ) : null}
            </div>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="profile-phone">{copy.phone}</Label>
            <Input
              id="profile-phone"
              type="tel"
              value={values.phone}
              onChange={(event) =>
                setValues((current) => ({ ...current, phone: event.target.value }))
              }
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {copy.cancel}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? copy.saving : copy.save}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ProviderFeedbackSection() {
  const { language } = useLanguage();
  const loadFeedback = useServerFn(getOwnProviderInternalFeedback);
  const saveFeedback = useServerFn(saveOwnProviderInternalFeedback);
  const [feedback, setFeedback] = useState<ProviderInternalFeedback | null>(null);

  useEffect(() => {
    void loadFeedback()
      .then((result) => setFeedback(result.feedback))
      .catch(() => undefined);
  }, [loadFeedback]);

  return (
    <CustomerFeedbackCard
      language={language}
      initialFeedback={feedback}
      onSave={async (rating, comment) => {
        const result = await saveFeedback({ data: { rating, comment, locale: language } });
        setFeedback(result.feedback);
        return result.feedback;
      }}
    />
  );
}

function OwnJobsSection() {
  const { language } = useLanguage();
  const copy = ownJobsCopy[language];
  const loadJobs = useServerFn(getOwnAccountJobs);
  const saveFeedback = useServerFn(saveOwnCustomerFeedback);
  const saveJob = useServerFn(updateOwnAccountJob);
  const [jobs, setJobs] = useState<AccountJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadJobs()
      .then((result) => {
        if (active) setJobs(result.jobs);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [loadJobs]);

  return (
    <section aria-labelledby="own-jobs-title">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">
            {authCopy[language].account.eyebrow}
          </p>
          <h2 id="own-jobs-title" className="mt-2 font-display text-3xl font-bold">
            {copy.title}
          </h2>
        </div>
        <Button asChild>
          <a href="/auftrag-einstellen">
            {copy.post}
            <ArrowRight className="size-4" />
          </a>
        </Button>
      </div>

      {loading ? (
        <Card className="mt-6 border-border/70">
          <CardContent className="flex items-center gap-3 p-6 text-muted-foreground">
            <span className="size-2 animate-pulse rounded-full bg-primary" />
            {authCopy[language].account.loading}
          </CardContent>
        </Card>
      ) : error ? (
        <p className="mt-6 rounded-xl bg-destructive/10 p-4 text-sm text-destructive">
          {copy.loadError}
        </p>
      ) : jobs.length === 0 ? (
        <Card className="mt-6 border-dashed border-border bg-card/70">
          <CardContent className="flex flex-col items-center p-10 text-center">
            <ClipboardList className="size-9 text-muted-foreground" />
            <p className="mt-4 text-muted-foreground">{copy.empty}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="mt-6 space-y-6">
          {jobs.map((job) => (
            <OwnJobCard
              key={job.submissionId}
              job={job}
              language={language}
              onSaveJob={async (values) => {
                await saveJob({ data: { submissionId: job.submissionId, ...values } });
                setJobs((current) =>
                  current.map((item) =>
                    item.submissionId === job.submissionId
                      ? { ...item, title: values.title, city: values.city, editable: values }
                      : item,
                  ),
                );
              }}
              onSaveFeedback={async (rating, comment) => {
                if (!job.jobId) throw new Error("Auftrag fehlt");
                const result = await saveFeedback({
                  data: { jobId: job.jobId, rating, comment, locale: language },
                });
                setJobs((current) =>
                  current.map((item) =>
                    item.submissionId === job.submissionId
                      ? { ...item, feedback: result.feedback }
                      : item,
                  ),
                );
                return result.feedback;
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function OwnJobCard({
  job,
  language,
  onSaveJob,
  onSaveFeedback,
}: {
  job: AccountJob;
  language: LanguageCode;
  onSaveJob: (values: AccountJob["editable"]) => Promise<void>;
  onSaveFeedback: Parameters<typeof CustomerFeedbackCard>[0]["onSave"];
}) {
  const copy = ownJobsCopy[language];
  const displayedStatus = job.jobStatus ?? job.submissionStatus;
  const statusLabel = copy.status[displayedStatus as keyof typeof copy.status] ?? displayedStatus;
  const canView = job.jobStatus === "published" && Boolean(job.slug);
  const canRate = job.jobStatus === "closed" || job.jobStatus === "expired";
  return (
    <div id={job.jobId ? `auftrag-${job.jobId}` : undefined} className="scroll-mt-24 space-y-4">
      <Card className="border-border/70 bg-card/95 shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col justify-between gap-5 md:flex-row md:items-start">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={job.jobStatus === "published" ? "default" : "secondary"}>
                  {statusLabel}
                </Badge>
                <span className="text-xs font-semibold text-muted-foreground">
                  {copy.reference}: {job.reference}
                </span>
              </div>
              <h3 className="mt-3 font-display text-xl font-bold">{job.title}</h3>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
                {job.city ? (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="size-4" /> {job.city}
                  </span>
                ) : null}
                <span className="inline-flex items-center gap-1.5">
                  <CalendarClock className="size-4" /> {copy.created}:{" "}
                  {new Date(job.createdAt).toLocaleDateString(`${language}-CH`)}
                </span>
              </div>
            </div>
            {canView ? (
              <div className="flex shrink-0 flex-wrap gap-2">
                <JobEditDialog job={job} onSave={onSaveJob} />
                <Button asChild variant="outline">
                  <a href={`/auftraege/${encodeURIComponent(job.slug as string)}`}>{copy.view}</a>
                </Button>
              </div>
            ) : (
              <JobEditDialog job={job} onSave={onSaveJob} />
            )}
          </div>
        </CardContent>
      </Card>
      {job.jobId && job.jobStatus !== "deleted" ? <JobCompanySelection jobId={job.jobId} /> : null}
      {canRate ? (
        <CustomerFeedbackCard
          language={language}
          initialFeedback={job.feedback}
          onSave={onSaveFeedback}
        />
      ) : null}
    </div>
  );
}

const jobEditCopy = {
  de: {
    edit: "Ausschreibung bearbeiten",
    title: "Ausschreibung bearbeiten",
    intro: "Passen Sie die Angaben an und speichern Sie Ihre Änderungen.",
    titleLabel: "Titel",
    description: "Beschreibung",
    city: "Ort",
    postalCode: "PLZ",
    category: "Kategorie",
    timeline: "Zeitraum",
    budget: "Budget",
    objectType: "Objekt",
    cancel: "Abbrechen",
    save: "Änderungen speichern",
    saving: "Wird gespeichert …",
    saved: "Ausschreibung wurde aktualisiert.",
    error: "Ausschreibung konnte nicht gespeichert werden.",
  },
  fr: {
    edit: "Modifier le mandat",
    title: "Modifier le mandat",
    intro: "Adaptez les informations et enregistrez vos modifications.",
    titleLabel: "Titre",
    description: "Description",
    city: "Lieu",
    postalCode: "NPA",
    category: "Catégorie",
    timeline: "Calendrier",
    budget: "Budget",
    objectType: "Objet",
    cancel: "Annuler",
    save: "Enregistrer",
    saving: "Enregistrement …",
    saved: "Mandat mis à jour.",
    error: "Le mandat n’a pas pu être enregistré.",
  },
  it: {
    edit: "Modifica incarico",
    title: "Modifica incarico",
    intro: "Aggiornate i dati e salvate le modifiche.",
    titleLabel: "Titolo",
    description: "Descrizione",
    city: "Località",
    postalCode: "NAP",
    category: "Categoria",
    timeline: "Periodo",
    budget: "Budget",
    objectType: "Oggetto",
    cancel: "Annulla",
    save: "Salva modifiche",
    saving: "Salvataggio …",
    saved: "Incarico aggiornato.",
    error: "Non è stato possibile salvare l’incarico.",
  },
  en: {
    edit: "Edit job",
    title: "Edit job",
    intro: "Update the details and save your changes.",
    titleLabel: "Title",
    description: "Description",
    city: "Location",
    postalCode: "Postcode",
    category: "Category",
    timeline: "Timeline",
    budget: "Budget",
    objectType: "Property type",
    cancel: "Cancel",
    save: "Save changes",
    saving: "Saving …",
    saved: "Job updated.",
    error: "The job could not be saved.",
  },
} as const;

function JobEditDialog({
  job,
  onSave,
}: {
  job: AccountJob;
  onSave: (values: AccountJob["editable"]) => Promise<void>;
}) {
  const { language } = useLanguage();
  const copy = jobEditCopy[language];
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState(job.editable);

  useEffect(() => setValues(job.editable), [job.editable]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await onSave(values);
      toast.success(copy.saved);
      setOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : copy.error);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Pencil className="mr-1.5 size-3.5" />
        {copy.edit}
      </Button>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <p className="text-sm text-muted-foreground">{copy.intro}</p>
        </DialogHeader>
        <form className="space-y-4" onSubmit={save}>
          <div className="space-y-2">
            <Label htmlFor={`job-title-${job.submissionId}`}>{copy.titleLabel}</Label>
            <Input
              id={`job-title-${job.submissionId}`}
              required
              value={values.title}
              onChange={(event) =>
                setValues((current) => ({ ...current, title: event.target.value }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`job-description-${job.submissionId}`}>{copy.description}</Label>
            <Textarea
              id={`job-description-${job.submissionId}`}
              required
              minLength={FORM_TEXT_MIN_LENGTH}
              maxLength={FORM_TEXT_MAX_LENGTH}
              rows={7}
              value={values.description}
              onChange={(event) =>
                setValues((current) => ({ ...current, description: event.target.value }))
              }
            />
            <FormTextCounter length={values.description.length} language={language} />
          </div>
          <div className="grid gap-4 sm:grid-cols-[1fr_10rem]">
            <div className="space-y-2">
              <Label htmlFor={`job-city-${job.submissionId}`}>{copy.city}</Label>
              <Input
                id={`job-city-${job.submissionId}`}
                required
                value={values.city}
                onChange={(event) =>
                  setValues((current) => ({ ...current, city: event.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`job-postal-${job.submissionId}`}>{copy.postalCode}</Label>
              <Input
                id={`job-postal-${job.submissionId}`}
                required
                value={values.postalCode}
                onChange={(event) =>
                  setValues((current) => ({ ...current, postalCode: event.target.value }))
                }
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor={`job-category-${job.submissionId}`}>{copy.category}</Label>
              <Input
                id={`job-category-${job.submissionId}`}
                required
                value={values.category}
                onChange={(event) =>
                  setValues((current) => ({ ...current, category: event.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`job-object-${job.submissionId}`}>{copy.objectType}</Label>
              <Input
                id={`job-object-${job.submissionId}`}
                required
                value={values.objectType}
                onChange={(event) =>
                  setValues((current) => ({ ...current, objectType: event.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`job-timeline-${job.submissionId}`}>{copy.timeline}</Label>
              <Input
                id={`job-timeline-${job.submissionId}`}
                required
                value={values.timeline}
                onChange={(event) =>
                  setValues((current) => ({ ...current, timeline: event.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`job-budget-${job.submissionId}`}>{copy.budget}</Label>
              <Input
                id={`job-budget-${job.submissionId}`}
                required
                value={values.budget}
                onChange={(event) =>
                  setValues((current) => ({ ...current, budget: event.target.value }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {copy.cancel}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? copy.saving : copy.save}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StatusCard({
  providerStatus,
  title,
  text,
  accessGranted,
  accessGrantedLabel,
  accessLockedLabel,
}: {
  providerStatus: ProviderStatus;
  title: string;
  text: string;
  accessGranted: boolean;
  accessGrantedLabel: string;
  accessLockedLabel: string;
}) {
  const positive = providerStatus === "approved";
  return (
    <Card
      className={cn(
        "overflow-hidden border-border/70 shadow-[0_20px_60px_-35px_hsl(var(--foreground)/0.28)]",
        positive && "border-emerald-500/20",
      )}
    >
      <CardContent className="p-0">
        <div className="grid sm:grid-cols-[auto_1fr]">
          <div
            className={cn(
              "flex min-h-36 items-center justify-center bg-amber-500/10 px-8 text-amber-700",
              positive && "bg-emerald-500/10 text-emerald-700",
              (providerStatus === "rejected" || providerStatus === "suspended") &&
                "bg-destructive/10 text-destructive",
            )}
          >
            {positive ? <CheckCircle2 className="size-11" /> : <Clock3 className="size-11" />}
          </div>
          <div className="p-6 sm:p-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-2xl font-bold">{title}</h2>
              <Badge variant={accessGranted ? "default" : "secondary"}>
                {accessGranted ? accessGrantedLabel : accessLockedLabel}
              </Badge>
            </div>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground">{text}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AccountField({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-border/70 bg-muted/25 p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground [&_svg]:size-4">
        {icon}
        {label}
      </div>
      <p className="mt-2 break-words font-semibold text-foreground">{value}</p>
    </div>
  );
}

function VerificationRow({
  icon,
  label,
  value,
  positive,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  positive: boolean;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border/70 bg-muted/25 p-3.5">
      <span
        className={cn(
          "grid size-9 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground [&_svg]:size-4",
          positive && "bg-emerald-500/10 text-emerald-700",
        )}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="truncate text-sm font-bold">{value}</p>
      </div>
    </div>
  );
}
